// 主人公の普段の致命の光の切り替え口 + 「傷口から光がほどけて抜ける」版 (2026-10-21・比較 ?lunaNormalFx=ribbon)。
//   配布版の既定 = A案 刺した点から青白い火花が放射状に散る (src/luna_spark_fx.ts・10-21 ユーザーが手本の絵を ok)。
//   ほどける光の版 (以下) は「リボンがリボンすぎた」で既定から外した。
//   作者 (10-20 夜): 「エフェクトが何だか全然わかんないです。もう一回始めからエフェクトに関しては作り直してほしい。」
//   前の版 = 光が漏れるひび (src/luna_lamp_leak_fx.ts drawLampCrack・?lunaNormalFx=crack で見られる)。
//   前の版が分からなかった理由と新しい見せ方 = docs/research/2026-10-21-luna-exec-fx-redo.md。
// 段: ① 刺した点の体に割れた穴が開き、穴の中だけが淡い青に光る (丸い閃光にしない = パリィと見分ける)
//     ② 穴から光のリボンがほどけ出て、主人公から離れる側の上へ昇る (主人公へは流さない・常夜燈の場所も示さない)
//     ③ リボンの先がちぎれて、かけらがひらひら昇って消える
// 質感は Codex の絵 (黒背景・加算で黒を消す・public/fx/luna-exec/・切り出し scripts/slice_exec_fx_sprites.py)。
//   出る・昇る・揺れる・消えるはコード。絵が読めていない間 (読み込み中・Vitest) は前の版 (ひび) で描く。
//   どれも時刻だけで決まる (乱数は種つき) = 同じ時刻なら同じ絵。
import { drawAdditive } from './exec_fx_sprites'
import { drawLampCrack, lampFlicker, type CrackInput } from './luna_lamp_leak_fx'
import { SPARK, drawLunaSparkFx, preloadSparkSprites, sparkSpritesReady } from './luna_spark_fx'
import { drawSparkMix, sparkMixLifeSec } from './luna_spark_mix_fx'

export type LunaNormalFx = 'spark' | 'unravel' | 'crack'
let look: LunaNormalFx = 'spark'
/** game.ts の URL ブロックだけが呼ぶ。ribbon = ほどける光 (10-21 朝)・crack = 光が漏れるひび (10-20)。それ以外・指定なし = 火花 (A案)。 */
export function setLunaNormalFx(v: string | null | undefined): LunaNormalFx {
  look = v === 'crack' || v === 'old' ? 'crack' : v === 'ribbon' || v === 'unravel' ? 'unravel' : 'spark'
  if (look === 'unravel') preloadUnravelSprites()
  if (look === 'spark') preloadSparkSprites()
  return look
}
export function lunaNormalFxLook(): LunaNormalFx { return look }

// 傷口 4コマ (開く・いちばん明るい・弱まる・暗い割れ目)。穴の中心 = 絵の真ん中。
export const WOUND_FRAMES = 4
// リボン 4種 × 2段 (つながっている・先がちぎれる)。根元 = 絵の下辺から RIBBON_ROOT_Y の真ん中。
export const RIBBON_KINDS = 4
// かけら 4種 × 回る3コマ (正面・傾き・横向き)。中心 = 絵の真ん中。
export const FLAKE_KINDS = 4
export const FLAKE_FRAMES = 3
// 切り出しの --align-bottom 620 --pad 10 → (620 - 10) / 620
export const RIBBON_ROOT_Y = 610 / 620

type Frame = { img: HTMLImageElement | null }
const wound: Frame[] = Array.from({ length: WOUND_FRAMES }, () => ({ img: null }))
const ribbon: Frame[] = Array.from({ length: RIBBON_KINDS * 2 }, () => ({ img: null }))
const flake: Frame[] = Array.from({ length: FLAKE_KINDS * FLAKE_FRAMES }, () => ({ img: null }))

let started = false
export function preloadUnravelSprites(prefix = '/fx/luna-exec/'): void {
  if (started || typeof Image === 'undefined') return
  started = true
  const load = (f: Frame, name: string): void => {
    const img = new Image()
    img.onload = () => { f.img = img }
    img.src = `${prefix}${name}.png`
  }
  wound.forEach((f, i) => load(f, `wound_${i}`))
  ribbon.forEach((f, i) => load(f, `ribbon_${i % RIBBON_KINDS}_${Math.floor(i / RIBBON_KINDS)}`))
  flake.forEach((f, i) => load(f, `flake_${i % FLAKE_KINDS}_${Math.floor(i / FLAKE_KINDS)}`))
}
function spritesReady(): boolean {
  return [wound, ribbon, flake].every(s => s.every(f => f.img !== null))
}

/** 時間の決まり (秒・当たりから)。 */
export const UNRAVEL = {
  /** 傷口の4コマの切り替わり (開く → いちばん明るい → 弱まる → 暗い割れ目 → 消える)。 */
  woundAt: [0, 0.05, 0.3, 0.62] as const,
  woundEndSec: 0.95,
  /** 傷口の大きさ (人の背丈 = 1 の時の高さ px)。 */
  woundH: 74,
  /** リボン: 本数・出る間隔・昇り切る秒・ちぎれ始め (寿命の割合)・高さ px。 */
  ribbons: 5,
  ribbonStagger: 0.045,
  ribbonLife: 0.95,
  ribbonTearAt: 0.42,
  ribbonH: 190,
  /** 昇る距離 px (寿命で)。 */
  ribbonRise: 150,
  /** かけら: 数・大きさ px・昇る距離 px・寿命。 */
  flakes: 14,
  flakeSize: 22,
  flakeRise: 150,
  flakeLife: 0.9,
  lifeSec: 1.6,
} as const

const clamp01 = (u: number): number => Math.max(0, Math.min(1, u))
function rnd(i: number, seed: number): number {
  const s = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453
  return s - Math.floor(s)
}
const easeOut = (u: number): number => 1 - Math.pow(1 - clamp01(u), 2)

/** 寿命 (game.ts の FX の掃除で使う)。選んだ版に合わせる。 */
export function lunaNormalFxLifeSec(crackLife: number): number {
  return look === 'crack' ? crackLife : look === 'spark' ? sparkMixLifeSec(SPARK.lifeSec) : UNRAVEL.lifeSec
}

/** 普段の致命の光 (世界の層・カメラの内側で描く)。入力は前の版のひびと同じ。絵が読めていない間 (読み込み中・Vitest) はひびで描く。 */
export function drawLunaNormalFx(ctx: CanvasRenderingContext2D, c: CrackInput): void {
  if (look === 'spark') { if (sparkSpritesReady()) { drawLunaSparkFx(ctx, c); drawSparkMix(ctx, c) } else drawLampCrack(ctx, c); return }
  if (look === 'crack' || !spritesReady()) { drawLampCrack(ctx, c); return }
  const age = c.ageSec
  if (age < 0 || age > UNRAVEL.lifeSec) return
  const S = c.scale
  // 主人公から離れる側 = 刺した向き (dir)。リボンはそちらへ傾いて昇る
  const away = c.dir
  drawWound(ctx, c.x, c.y, age, S, away)
  for (let i = 0; i < UNRAVEL.ribbons; i++) drawRibbon(ctx, c.x, c.y, age - i * UNRAVEL.ribbonStagger, S, away, i)
  for (let i = 0; i < UNRAVEL.flakes; i++) drawFlake(ctx, c.x, c.y, age, S, away, i)
}

function drawWound(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, S: number, away: 1 | -1): void {
  if (age > UNRAVEL.woundEndSec) return
  const at = UNRAVEL.woundAt
  let k = 0
  for (let i = at.length - 1; i >= 0; i--) if (age >= at[i]!) { k = i; break }
  const img = wound[k]!.img!
  const a = k < 3 ? 1 : 1 - clamp01((age - at[3]) / (UNRAVEL.woundEndSec - at[3]))
  // 開いた一瞬だけ少し大きく (刺さった衝撃)
  const pop = age < 0.08 ? 1.15 - 0.15 * (age / 0.08) : 1
  const h = UNRAVEL.woundH * S * pop
  const w = h * (img.width / img.height)
  // 穴は刺した向きへ少し傾ける (縦に長い穴が、突きの向きに沿って開く)
  drawAdditive(ctx, img, x, y, w, h, 0.5, 0.5, a * lampFlicker(age + 0.3), 0.18 * away, away < 0)
}

function drawRibbon(ctx: CanvasRenderingContext2D, x: number, y: number, t: number, S: number, away: 1 | -1, i: number): void {
  if (t < 0 || t > UNRAVEL.ribbonLife) return
  const u = t / UNRAVEL.ribbonLife
  const kind = Math.floor(rnd(i, 3) * RIBBON_KINDS)
  // 傾き: 真上から、主人公から離れる側へ 10〜55 度。1本だけ真上寄り
  const tilt = (0.18 + rnd(i, 5) * 0.78) * away * (i === 0 ? 0.3 : 1)
  const rise = UNRAVEL.ribbonRise * S * easeOut(u * 1.15) * (0.75 + rnd(i, 7) * 0.5)
  // 根元は傷口から少しずつ離れて昇る (ほどけて抜ける)
  const rx = x + Math.sin(tilt) * rise + Math.sin(t * 6 + i) * 3 * S
  const ry = y - Math.cos(tilt) * rise
  const grow = easeOut(u / 0.3)
  const h = UNRAVEL.ribbonH * S * (0.7 + rnd(i, 9) * 0.45) * (0.35 + 0.65 * grow)
  const torn = u >= UNRAVEL.ribbonTearAt
  const img = ribbon[kind + (torn ? RIBBON_KINDS : 0)]!.img!
  const w = h * (img.width / img.height)
  const fadeIn = clamp01(t / 0.05)
  const fadeOut = 1 - clamp01((u - 0.6) / 0.4)
  // ちぎれに替わる所は 0.06 秒で重ねて替える (ぱっと替わって見えないように)
  const blend = clamp01((u - UNRAVEL.ribbonTearAt) / 0.06)
  const a = fadeIn * fadeOut * 0.95
  const sway = Math.sin(t * 5 + i * 1.7) * 0.08
  const flip = rnd(i, 13) < 0.5
  if (torn && blend < 1) {
    const prev = ribbon[kind]!.img!
    drawAdditive(ctx, prev, rx, ry, w, h, 0.5, RIBBON_ROOT_Y, a * (1 - blend), tilt + sway, flip)
  }
  drawAdditive(ctx, img, rx, ry, w, h, 0.5, RIBBON_ROOT_Y, a * (torn ? blend : 1), tilt + sway, flip)
}

function drawFlake(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, S: number, away: 1 | -1, i: number): void {
  // かけらはリボンがちぎれる頃に、リボンの先のあたりから生まれる
  const born = UNRAVEL.ribbonLife * UNRAVEL.ribbonTearAt + rnd(i, 21) * 0.35
  const t = age - born
  if (t < 0 || t > UNRAVEL.flakeLife) return
  const u = t / UNRAVEL.flakeLife
  const tilt = (0.1 + rnd(i, 23) * 0.9) * away
  const startUp = UNRAVEL.ribbonH * S * (0.5 + rnd(i, 25) * 0.5) + UNRAVEL.ribbonRise * S * 0.5
  const up = startUp + UNRAVEL.flakeRise * S * easeOut(u) * (0.6 + rnd(i, 27) * 0.6)
  const fx = x + Math.sin(tilt) * up + Math.sin(t * 4 + i * 2.1) * 12 * S
  const fy = y - Math.cos(tilt) * up
  const kind = Math.floor(rnd(i, 29) * FLAKE_KINDS)
  // 回る: 正面 → 傾き → 横 → 傾き → 正面 …
  const spin = Math.floor((t * (7 + rnd(i, 31) * 5) + rnd(i, 33) * 4)) % 4
  const frame = spin === 3 ? 1 : spin
  const img = flake[kind + frame * FLAKE_KINDS]!.img!
  const size = UNRAVEL.flakeSize * S * (0.7 + rnd(i, 35) * 0.6)
  const fw = size * (img.width / img.height)
  const a = clamp01(t / 0.06) * (1 - u) * (1 - u * 0.3)
  drawAdditive(ctx, img, fx, fy, fw, size, 0.5, 0.5, a, t * (1.5 + rnd(i, 37) * 2) * away, rnd(i, 39) < 0.5)
}
